import {useState, useEffect} from "react";
import { useGetIdentity, useDataProvider, useGetList, usePermissions } from "react-admin";
import { styled } from '@mui/material/styles';
import {
    Card,
    CardContent,
CardHeader,
Box,
Paper,
Grid,
Typography,
Stack,
}  from '@mui/material';
import { authenticatedApi } from "../dataProvider";
import { format, parseISO } from "date-fns";
import { Link, useNavigate } from 'react-router-dom';
import { CommunityList } from "../Resources/Communities";
import { FormEntryList } from "../Resources/FormEntries";
import { DefaultizedPieValueType } from '@mui/x-charts';
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';
import { formEntryStatuses, round } from "../helpers/strings";

function Title(props) {
    return (
      <Typography component="h2" variant="h6" color="primary" gutterBottom>
        {props.children}
      </Typography>
    );
}

function DashboardItem(props) {
    const Cell = () => (<Paper
        sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        // height: 240,
        // backgroundColor: props.color ?? '#fff',
        background: `
            ${props.color ?? '#fff'}
            radial-gradient(
                12vw circle at 108% 0px,
                ${props.color ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.04)'} 50%,
                ${props.color ?? '#fff'} 51%
            )
        `,
        color: props.textColor ?? '#000',
        '*': {
            color: `${props.textColor ?? '#000'} !important`,
        }
        }}
    >
    {props.children}
    </Paper>);

    const WrappedCell = () => props.to ? (
        <Link style={{flexBasis: '100%', textDecoration: 'none'}} to={props.to}><Cell/></Link>
    ) : (<Cell/>);

    return (
        <Grid item xs={12} md={4} lg={4}>
            <WrappedCell/>
        </Grid>
    );
}


const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  }));

  const sizing = {
    // margin: { right: 5 },
    // width: 500,
    // height: 300,
    slotProps: {
        legend2: {
            // hidden: true
            direction: 'row',
            position: { vertical: 'top', horizontal: 'middle' },
            padding: 0,
            /*
            position: {
                vertical: 'middle',
                horizontal: 'right',
            },
            */
            itemMarkWidth: 20,
            itemMarkHeight: 2,
            markGap: 5,
            itemGap: 10,
        },
    },
  };

  function PieChartWithCustomizedLabel(props) {
    const navigate = useNavigate();

    const legend = {
        'not-started': 'Not Started',
        // 'in-progress': 'In Progress',
        ...formEntryStatuses,
    }
    const data = Object.entries(props.data).filter(data => data[0] != 'total').map(dataPoint => ({
        id: dataPoint[0],
        label: legend[dataPoint[0]] ?? dataPoint[0],
        value: dataPoint[1],
    }));
    const TOTAL = data.map((item) => item.value).reduce((a, b) => a + b, 0);

    const getArcLabel = (params: DefaultizedPieValueType) => {
    const percent = params.value / TOTAL;
    return `${(percent * 100).toFixed(0)}%`;
    };

    const handleClick = (event, itemIdentifier, item) => {
        let search = null;
        // not started

        if(formEntryStatuses.hasOwnProperty(item.id)) {
            navigate({
                pathname: '/form-entries',
                search: `filter=${JSON.stringify({ status: item.id })}`,
            });
        } else if(item.id === 'not-started') {
            navigate({
                pathname: '/communities',
                search: `filter=${JSON.stringify({ status: 'not-started' })}`,
            });
        }
    };

    return (
      <PieChart
          colors={['#6B7280', '#00ABCC', '#F9A51A', '#FFDD00', '#8BC53F']}
          series={[
            {
                paddingAngle: 5,
                innerRadius: 60,
                outerRadius: 80,
                data,
            },
          ]}
          onClick={handleClick}
          series2={[
            {
            data,
            innerRadius: 30,
            outerRadius: 100,
            paddingAngle: 5,
            cornerRadius: 5,
            startAngle: -45,
            endAngle: 315,
            // cx: 150,
            // cy: 150,
            }
          ]}
        /*
        sx={{
          [`& .${pieArcLabelClasses.root}`]: {
            fill: 'white',
            fontSize: 14,
          },
        }}
        */
        {...sizing}
      />
    );
  }

const Dashboard = () => {
    const { permissions } = usePermissions();
    const { data: identity, isLoading: identityLoading } = useGetIdentity();
    const [dashboardStats, setDashboardStats] = useState({
        active_form: null,
        progress: {
            'not-started': 0,
            'draft': 0,
            'in-review': 0,
            'needs-attention': 0,
            'accepted': 0,
        },
        'support-requests': {
            'open': 0,
        },
    });

    useEffect(() => {
        if(identityLoading || !identity) return;
      authenticatedApi.get('/dashboard-stats').then(response => {
          setDashboardStats(response.data);
      });
    }, [/*identityLoading, */identity]);

    if(identityLoading) {
        return 'Loading...';
    }

    if(!dashboardStats.active_form) {
        return <em>No active form</em>;
    }

    const dashboardStatNum = (stat) => {
        const statNum = dashboardStats.progress[stat];
        const total = dashboardStats.progress['total'];

        if(statNum === null) {
            return (<em>N/A</em>)
        }

        const percent = total === 0 ? 0 : Math.round((statNum / total) * 100);

        return (
            <Stack direction="row" spacing={4} justifyContent={'space-between'} alignItems={'flex-end'} lineHeight={1}>
                <Typography component="span" variant="p">{percent}%</Typography>
                <Typography component="span" variant="p" fontSize={"60%"}>{statNum}</Typography>
            </Stack>
        )
    };

    return (
        <Box sx={{marginTop: '1em'}}>
            <Typography variant="h4" component="div" gutterBottom>
                {identity ? `Welcome, ${identity.name}!` : 'Welcome Back!'}
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12} md={6} lg={4}>
                    {/*
                    <Paper
                        sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        // height: 240,
                        }}
                    >
                    */}
                        <PieChartWithCustomizedLabel data={dashboardStats.progress} />
                    {/* </Paper> */}
                </Grid>
                <Grid item xs={12} md={6} lg={8}>
                {/*  */}
                    <Grid container spacing={2} columns={12}>
                        {/* TODO: make "not started" actually load a list of communities */}
                        <DashboardItem color="#6B7280" textColor="#fff" to={{
                            pathname: '/communities',
                            search: `filter=${JSON.stringify({ status: 'not-started', form_id: dashboardStats.active_form.id })}`,
                        }}>
                            <Title>Not Started</Title>
                            <Typography component="p" variant="h4">
                                {dashboardStatNum('not-started')}
                            </Typography>
                        </DashboardItem>

                        <DashboardItem color="#00ABCC" textColor="#fff" to={{
                            pathname: '/form-entries',
                            search: `filter=${JSON.stringify({ status: 'draft', form_id: dashboardStats.active_form.id })}`,
                        }}>
                            <Title>In Progress</Title>
                            <Typography component="p" variant="h4">
                                {dashboardStatNum('draft')}
                            </Typography>
                        </DashboardItem>

                        <DashboardItem color="#F9A51A" textColor="#fff" to={{
                            pathname: '/form-entries',
                            search: `filter=${JSON.stringify({ status: 'in-review', form_id: dashboardStats.active_form.id })}`,
                        }}>
                            <Title>In Review</Title>
                            <Typography component="p" variant="h4">
                                {dashboardStatNum('in-review')}
                            </Typography>
                        </DashboardItem>

                        <DashboardItem color="#FFDD00" textColor="#000" to={{
                            pathname: '/form-entries',
                            search: `filter=${JSON.stringify({ status: 'needs-attention', form_id: dashboardStats.active_form.id })}`,
                        }}>
                            <Title>Needs Attention</Title>
                            <Typography component="p" variant="h4">
                                {dashboardStatNum('needs-attention')}
                            </Typography>
                        </DashboardItem>

                        <DashboardItem color="#8BC53F" textColor="#fff" to={{
                            pathname: '/form-entries',
                            search: `filter=${JSON.stringify({ status: 'accepted', form_id: dashboardStats.active_form.id })}`, //submitted,
                        }}>
                            <Title>Accepted</Title>
                            <Typography component="p" variant="h4">
                                {dashboardStatNum('accepted')}
                            </Typography>
                        </DashboardItem>

                        <DashboardItem to={{
                            pathname: '/support-requests',
                            // search: `filter=${JSON.stringify({ status: 'open' })}`, // don't do this because there's no way to undo the filter
                        }}>
                            <Title>Awaiting Support</Title>
                            <Typography component="p" variant="h4" lineHeight={1}>
                                <Stack direction="row" spacing={1} justifyContent={'flex-start'} alignItems={'center'} lineHeight={1}>
                                    <Typography component="span" variant="p">
                                        {dashboardStats['support-requests']['open'] === null && (<em>N/A</em>)}
                                        {dashboardStats['support-requests']['open'] !== null && dashboardStats['support-requests']['open']}
                                    </Typography>
                                    <Typography component="span" variant="p" fontSize={"60%"} color={'#555 !important'}>{dashboardStats['support-requests']['open'] === 1 ? 'ticket' : 'tickets'}</Typography>
                                </Stack>
                            </Typography>
                        </DashboardItem>
                    </Grid>
                </Grid>
            </Grid>

            <Grid container spacing={2}>
                <Grid item xs={12} md={12} lg={12} sx={{'& .list-page > .MuiPaper-root': {marginTop: '0 !important'}}}>
                    <Typography variant="h5" component="div" sx={{marginTop: 4, marginBottom: 1}} gutterBottom>
                        Form Entries
                    </Typography>

                    <FormEntryList resource={'form-entries'} actions={null} hasCreate={false} exporter={false} sort={{ field: 'created_at', order: 'DESC' }} pagination={false} />
                </Grid>
            </Grid>

            {/*
            <Typography variant="p">
            <br/>Ability to edit “ownership” of a community
            <br/>Ability to update billing for a community


            <br/>Community Metrics** 6-8 hours
            <br/>Number of dropped communities in last 7 days
            <br/>Number of dropped communities in last 30 days


            <br/>Number of Active Communities
            <br/>Number of Active Communities by State/Coordinating Program
            <br/>User Metrics
            <br/>Number of users within the last 24 hours
            <br/>Number of users within the last 7 days
            <br/>Number of users within the last 30 days
            <br/>Number of users who have not logged in 60 days (maybe 45/90? thoughts)?

            <br/>Metrics on Monthly billing?


            <br/>Recent failed billing??
            </Typography>
            */}

        </Box>
    )
};

export default Dashboard;
